// The review queue: every active product still waiting for a OneBill decision,
// closest match first, and the one in hand with its OneBill suggestions. Each
// decision saves at once and moves to the next product. Create in OneBill first
// asks which OneBill plan's setup the new product copies (#2156).
import React, { useState } from "react";
import { formatCents } from "@phoneware/lens-domain";
import { Icon } from "../../icons.js";
import { Chip } from "../../ui.js";
import { useCatalog } from "./context.js";
import { billedText, categoriesOf, categoryOrder, copyNote, kindWord, pct, scoreClass, type Product } from "./model.js";
import { OneBillPicker } from "./parts.js";

type Sort = "best" | "cat" | "name";

export function ReviewQueue({ scores }: { scores: Record<string, number> }): React.ReactElement {
  const { list, link, decide, open, busy, links } = useCatalog();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState<Sort>("best");
  const [limit, setLimit] = useState(40);
  const [selected, setSelected] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  // The product whose Create in OneBill is asking what to copy; any other product links as usual.
  const [copying, setCopying] = useState<string | null>(null);

  const queue = list.filter((p) => p.active && p.billingLink.state === "unreviewed");
  if (queue.length === 0) {
    return (
      <section className="card">
        <div className="empty" data-testid="review-empty">
          The review queue is empty. Every active product has a OneBill decision.
        </div>
      </section>
    );
  }

  const q = query.trim().toLowerCase();
  const score = (p: Product) => scores[p.id] ?? 0;
  const shown = queue
    .filter((p) => !q || `${p.name} ${p.code} ${p.autotask?.id ?? ""}`.toLowerCase().includes(q))
    .filter((p) => !category || p.category === category)
    .sort((a, b) =>
      sort === "best" ? score(b) - score(a) || a.name.localeCompare(b.name) : sort === "name" ? a.name.localeCompare(b.name) : categoryOrder(a.category, b.category) || a.name.localeCompare(b.name),
    );
  const page = shown.slice(0, limit);
  const current = shown.find((p) => p.id === selected) ?? shown[0] ?? null;
  const position = current ? shown.indexOf(current) : -1;

  /** After a decision, the next product down the list, or the one above when it was the last. */
  function advance(from: Product): void {
    const i = shown.indexOf(from);
    setSelected((shown[i + 1] ?? shown[i - 1] ?? null)?.id ?? null);
    setDone((n) => n + 1);
  }

  return (
    <div className="rq">
      <section className="card rq-list" aria-label="Products to review">
        <div className="rq-h">
          <label className="search">
            <Icon name="search" size={15} />
            <span className="sr">Search the queue</span>
            <input className="input slim" type="search" data-testid="rq-search" placeholder={`Search ${queue.length.toLocaleString("en-US")} products by name or id`} value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          <div className="row2">
            <select className="input slim" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {categoriesOf(queue).map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <select className="input slim" aria-label="Order" data-testid="rq-sort" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              <option value="best">Closest match first</option>
              <option value="cat">By category</option>
              <option value="name">By name</option>
            </select>
          </div>
        </div>
        <ul className="rq-items" data-testid="rq-items">
          {page.length ? (
            page.map((p) => (
              <li key={p.id}>
                <button type="button" aria-current={p.id === current?.id} data-testid={`rq-${p.id}`} onClick={() => setSelected(p.id)}>
                  <span className="rq-txt">
                    <span className="nm">{p.name}</span>
                    <span className="mt">
                      {p.category} · {kindWord(p)} {p.autotask?.id ?? ""} · {billedText(p)}
                    </span>
                  </span>
                  <span className={`sc ${scoreClass(score(p))}`}>{score(p) ? pct(score(p)) : "None"}</span>
                </button>
              </li>
            ))
          ) : (
            <li className="rq-more">Nothing in the queue matches.</li>
          )}
          {shown.length > page.length ? (
            <li className="rq-more">
              <button className="link" type="button" onClick={() => setLimit(limit + 40)}>
                Show 40 more of {shown.length.toLocaleString("en-US")}
              </button>
            </li>
          ) : null}
        </ul>
      </section>
      {current ? (
        <section className="card rq-item" data-testid="rq-item" data-product={current.id}>
          <div className="rq-pos">
            <Icon name="triangle-alert" size={14} />
            <span>
              <b>{queue.length.toLocaleString("en-US")}</b> need a OneBill link{done ? ` \u00b7 ${done} decided here` : ""}
            </span>
            <span className="grow" />
            <span>
              {position + 1} of {shown.length.toLocaleString("en-US")}
            </span>
          </div>
          <h2>{current.name}</h2>
          <div className="rq-meta">
            <Chip kind="quiet">
              {kindWord(current)} {current.autotask?.id ?? ""}
            </Chip>
            {current.code ? <Chip kind="quiet">{current.code}</Chip> : null}
            <Chip>{current.category}</Chip>
            {current.billing ? (
              <Chip>
                {billedText(current)} · {formatCents(current.listPrice)}
              </Chip>
            ) : (
              <Chip kind="warn" icon="clock">
                {billedText(current)} · {formatCents(current.listPrice)}
              </Chip>
            )}
          </div>
          {current.description ? <p className="rq-desc">{current.description}</p> : null}
          {current.billing ? null : (
            <div className="callout warn gap" data-testid="rq-yearly">
              <Icon name="clock" size={15} />
              <span>
                <b>{billedText(current)}.</b> Omni quotes monthly and one-time only, so dealers can{"\u2019"}t quote this whatever you link. Link it now if OneBill has it, or mark it Not quoted in
                Omni.
              </span>
            </div>
          )}
          {links && copying === current.id && !current.description.trim() ? (
            <p className="hint ob-warn" data-testid="rq-no-description">
              <Icon name="triangle-alert" size={12} />
              <span>OneBill requires a description, and this product has none. Open the product to add one before the next sync.</span>
            </p>
          ) : null}
          {links && copying === current.id ? (
            <OneBillPicker
              key={`${current.id}:copy`}
              p={current}
              where="rq"
              busy={busy}
              mode="copy"
              onCopy={(productCode, pricePlanId) => {
                void link(current, { state: "create", copyFrom: { productCode, pricePlanId } }, copyNote(current.name, { productCode, pricePlanId })).then((saved) => {
                  if (saved) advance(current);
                });
              }}
            />
          ) : links ? (
            <OneBillPicker
              key={current.id}
              p={current}
              where="rq"
              busy={busy}
              onLink={(productCode, pricePlanId) => {
                void link(current, { state: "linked", onebill: { productCode, pricePlanId } }, `Linked ${current.name} to OneBill ${productCode}, plan ${pricePlanId}.`).then((saved) => {
                  if (saved) advance(current);
                });
              }}
            />
          ) : (
            <p className="card-p ro" data-testid="rq-view-only">
              <Icon name="lock" size={14} />
              <span>Billing ops or a Super admin links it to OneBill.</span>
            </p>
          )}
          <div className="acts">
            {links && copying === current.id ? (
              <button className="btn" type="button" data-testid="rq-copy-back" onClick={() => setCopying(null)}>
                <Icon name="arrow-left" size={15} />
                Back to linking
              </button>
            ) : links ? (
              <>
                <button
                  className="btn"
                  type="button"
                  disabled={busy}
                  data-testid="rq-create"
                  onClick={() => setCopying(current.id)}
                >
                  <Icon name="plus" size={15} />
                  Create in OneBill
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={busy}
                  data-testid="rq-notq"
                  onClick={() => void decide(current, "not_billed").then((saved) => saved && advance(current))}
                >
                  <Icon name="ban" size={15} />
                  Not quoted in Omni
                </button>
              </>
            ) : null}
            <span className="grow" />
            <button className="btn white" type="button" onClick={() => open(current.id)}>
              Open product
            </button>
            <button className="btn white" type="button" data-testid="rq-skip" onClick={() => setSelected((shown[position + 1] ?? shown[0]).id)}>
              Skip
              <Icon name="chevron-right" size={15} />
            </button>
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="empty">Nothing matches the search.</div>
        </section>
      )}
    </div>
  );
}
